import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Heart, Clock, FileText, MessageSquare, Crown, CheckCircle2, Download, Edit3, Bell, Sparkles } from "lucide-react";
import LeaseDealCard from "@/components/LeaseDealCard";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

export default function Dashboard() {
  const { user } = useAuth();
  const [favs, setFavs] = useState([]);
  const [agreements, setAgreements] = useState([]);
  const [billing, setBilling] = useState(null);
  const [saved, setSaved] = useState([]);
  const [notifs, setNotifs] = useState([]);

  const loadNotifs = () => api.get("/notifications").then((r) => setNotifs(r.data)).catch(() => {});

  useEffect(() => {
    Promise.all([
      api.get("/favorites").then((r) => setFavs(r.data)).catch(() => {}),
      api.get("/agreements").then((r) => setAgreements(r.data)).catch(() => {}),
      api.get("/billing/me").then((r) => setBilling(r.data)).catch(() => {}),
      api.get("/saved-searches").then((r) => setSaved(r.data)).catch(() => {}),
      loadNotifs(),
    ]);
  }, []);

  const pendingAgs = agreements.filter((a) => a.status !== "signed");
  const signedAgs = agreements.filter((a) => a.status === "signed");
  const unreadNotifs = notifs.filter((n) => !n.read);

  const [tab, setTab] = useState("favorites");
  const [tabAutoSet, setTabAutoSet] = useState(false);
  useEffect(() => {
    if (!tabAutoSet && unreadNotifs.length > 0) {
      setTab("alerts");
      setTabAutoSet(true);
    }
  }, [unreadNotifs.length, tabAutoSet]);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setNotifs((arr) => arr.map((n) => (n.id === id ? { ...n, read: true } : n)));
  };
  const markAllRead = async () => {
    await api.post("/notifications/read-all");
    setNotifs((arr) => arr.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-12" data-testid="dashboard-page">
      <p className="label-mono mb-3">Your Concierge</p>
      <h1 className="font-display text-4xl md:text-5xl font-light tracking-tighter mb-2">Hi, {user?.name?.split(" ")[0] || "there"}.</h1>
      <p className="text-gray-400 mb-10">Track your active vehicle search, signed agreements, and concierge access.</p>

      <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-10">
        <Stat icon={Heart} label="Saved Deals" value={favs.length} testId="stat-favorites" />
        <Stat icon={FileText} label="Agreements" value={agreements.length} testId="stat-agreements" />
        <Stat icon={Bell} label="Saved Searches" value={saved.length} testId="stat-saved" />
        <Stat icon={Sparkles} label="Alerts" value={unreadNotifs.length} testId="stat-alerts" highlight={unreadNotifs.length > 0} />
        <Stat icon={Crown} label="Membership" value={billing?.tier ? billing.tier.toUpperCase() : "—"} testId="stat-tier" />
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-[#0A0A0E] border border-[#1A1A24] mb-6 flex-wrap h-auto">
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            Alerts {unreadNotifs.length > 0 && <span className="ml-1.5 text-[10px] silver-gradient text-[#050507] rounded-full px-1.5">{unreadNotifs.length}</span>}
          </TabsTrigger>
          <TabsTrigger value="favorites" data-testid="tab-favorites">Saved Deals</TabsTrigger>
          <TabsTrigger value="agreements" data-testid="tab-agreements">Agreements {pendingAgs.length > 0 && <span className="ml-1.5 text-[10px] silver-gradient text-[#050507] rounded-full px-1.5">{pendingAgs.length}</span>}</TabsTrigger>
          <TabsTrigger value="subscription" data-testid="tab-subscription">Membership</TabsTrigger>
          <TabsTrigger value="saved" data-testid="tab-saved">Saved Searches</TabsTrigger>
          <TabsTrigger value="progress" data-testid="tab-progress">Pipeline</TabsTrigger>
        </TabsList>

        <TabsContent value="alerts">
          {notifs.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl text-gray-400" data-testid="alerts-empty">
              No alerts yet. Save a search and we'll notify you the moment a matching verified deal goes live.
              <div className="mt-5"><Link to="/lease-deals" className="px-5 py-2.5 rounded-full silver-gradient text-[#050507] text-xs font-bold">Browse Deals</Link></div>
            </div>
          ) : (
            <>
              {unreadNotifs.length > 0 && (
                <div className="flex justify-end mb-3">
                  <button onClick={markAllRead} className="text-xs text-gray-400 hover:text-white" data-testid="alerts-mark-all-read">Mark all read</button>
                </div>
              )}
              <div className="space-y-3">
                {notifs.map((n) => (
                  <Link
                    key={n.id}
                    to={n.deal_id ? `/lease-deals/${n.deal_id}` : "/lease-deals"}
                    onClick={() => !n.read && markRead(n.id)}
                    className={`block rounded-2xl p-5 border transition ${n.read ? "bg-[#0A0A0E] border-[#1A1A24]" : "bg-emerald-500/[0.04] border-emerald-500/30"}`}
                    data-testid={`alert-${n.id}`}
                  >
                    <div className="flex items-start gap-3">
                      <Sparkles className={`w-5 h-5 mt-0.5 ${n.read ? "text-gray-500" : "text-emerald-300"}`} />
                      <div className="flex-1">
                        <div className="font-display text-base">{n.title}</div>
                        <div className="text-xs text-gray-400 mt-1">{n.body}</div>
                        <div className="text-[10px] text-gray-500 mt-2">{new Date(n.created_at).toLocaleString()}</div>
                      </div>
                      {!n.read && <span className="w-2 h-2 rounded-full bg-emerald-400 mt-2" />}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </TabsContent>

        <TabsContent value="favorites">
          {favs.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl">
              <p className="text-gray-400 mb-5">You haven't saved any deals yet.</p>
              <Link to="/lease-deals" className="px-6 py-3 rounded-full silver-gradient text-[#050507] font-bold">Browse Lease Deals</Link>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {favs.map((d) => <LeaseDealCard key={d.id} deal={d} favorited />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="agreements">
          {agreements.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl text-gray-400">
              No agreements yet. Once your broker prepares an agreement, you'll see it here for signing.
            </div>
          ) : (
            <div className="space-y-3">
              {pendingAgs.length > 0 && <div className="label-mono">Pending Signature</div>}
              {pendingAgs.map((a) => (
                <div key={a.id} className="bg-[#0A0A0E] border border-yellow-500/30 rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`agreement-pending-${a.id}`}>
                  <div>
                    <div className="font-display text-lg">{a.title}</div>
                    {a.deal_summary && <div className="text-xs text-gray-500 mt-1">{a.deal_summary}</div>}
                    <div className="text-[10px] text-gray-500 mt-2">Created {new Date(a.created_at).toLocaleDateString()}</div>
                  </div>
                  <Link to={`/agreements/${a.id}`} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full silver-gradient text-[#050507] text-xs font-bold whitespace-nowrap" data-testid={`agreement-sign-link-${a.id}`}>
                    <Edit3 className="w-3.5 h-3.5" /> Sign Now
                  </Link>
                </div>
              ))}
              {signedAgs.length > 0 && <div className="label-mono mt-7">Signed</div>}
              {signedAgs.map((a) => (
                <div key={a.id} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5 flex items-center justify-between gap-4" data-testid={`agreement-signed-${a.id}`}>
                  <div>
                    <div className="font-display text-lg flex items-center gap-2">{a.title} <CheckCircle2 className="w-4 h-4 text-emerald-400" /></div>
                    <div className="text-xs text-gray-500 mt-1">Signed by {a.signer_name} on {new Date(a.signed_at).toLocaleString()}</div>
                  </div>
                  {a.signed_pdf_path && (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.signed_pdf_path}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-xs hover:bg-white/5" data-testid={`agreement-download-${a.id}`}>
                      <Download className="w-3.5 h-3.5" /> PDF
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="subscription">
          <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-7" data-testid="subscription-card">
            {billing?.tier && billing?.status === "active" ? (
              <>
                <Crown className="w-6 h-6 text-silver mb-3" />
                <div className="label-mono mb-2">Your Tier</div>
                <div className="font-display text-3xl mb-2">{billing.tier.toUpperCase()}</div>
                <div className="text-sm text-emerald-400 mb-1">Active</div>
                <div className="text-xs text-gray-500 mb-6">
                  Activated {billing.activated_at ? new Date(billing.activated_at).toLocaleDateString() : "—"}
                  {billing.expires_at && <> · Expires {new Date(billing.expires_at).toLocaleDateString()}</>}
                </div>
                <Link to="/concierge" className="inline-flex px-5 py-2.5 rounded-full border border-white/15 text-sm hover:bg-white/5" data-testid="subscription-manage">Renew or Upgrade</Link>
              </>
            ) : (
              <>
                <Crown className="w-6 h-6 text-silver mb-3" />
                <div className="font-display text-2xl mb-2">No active membership</div>
                <p className="text-sm text-gray-400 mb-5">Get deal alerts, priority broker access, and AI tool credits.</p>
                <Link to="/concierge" className="inline-flex px-5 py-2.5 rounded-full silver-gradient text-[#050507] font-bold text-sm" data-testid="subscription-explore">Explore Tiers</Link>
              </>
            )}
          </div>
        </TabsContent>

        <TabsContent value="saved">
          {saved.length === 0 ? (
            <div className="text-center py-16 border border-dashed border-white/10 rounded-2xl text-gray-400">
              No saved searches yet. Save a marketplace filter set to get alerts when matching deals appear.
            </div>
          ) : (
            <div className="space-y-3">
              {saved.map((s) => (
                <div key={s.id} className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-5" data-testid={`saved-search-${s.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-display text-lg">{s.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{Object.entries(s.filters).map(([k, v]) => `${k}=${v}`).join(" · ")}</div>
                    </div>
                    <button onClick={async () => { await api.delete(`/saved-searches/${s.id}`); setSaved(saved.filter((x) => x.id !== s.id)); }} className="text-xs text-gray-500 hover:text-white" data-testid={`saved-delete-${s.id}`}>Delete</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="progress">
          <div className="bg-[#0A0A0E] border border-[#1A1A24] rounded-2xl p-7">
            <div className="font-display text-xl mb-4">Deal Pipeline</div>
            <div className="space-y-4">
              {["Discovery", "Sourcing", "Negotiation", "Delivery"].map((s, i) => (
                <div key={s} className="flex items-center gap-4">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? "silver-gradient text-[#050507]" : "bg-white/5 text-gray-500"}`}>{i+1}</div>
                  <div>
                    <div className="text-sm">{s}</div>
                    <div className="text-xs text-gray-500">{i === 0 ? "Submit your first request to start" : "Pending"}</div>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/find-my-car" className="mt-6 inline-flex px-5 py-3 rounded-full silver-gradient text-[#050507] font-bold text-sm">Start a Request</Link>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ icon: Icon, label, value, testId, highlight }) {
  return (
    <div className={`bg-[#0A0A0E] border rounded-2xl p-5 ${highlight ? "border-emerald-500/40" : "border-[#1A1A24]"}`} data-testid={testId}>
      <Icon className={`w-4 h-4 mb-3 ${highlight ? "text-emerald-300" : "text-silver"}`} />
      <div className="label-mono mb-1">{label}</div>
      <div className="font-display text-2xl">{value}</div>
    </div>
  );
}
